import React from 'react';
import { numberFormatter } from '../../helpers/formatters';
import { OperationChain, Scenario } from '../../types';
import { UncontrolledTooltip } from 'reactstrap';
import { HelpCircleIcon } from '@iconicicons/react';
import { LinkToRates } from '../../helpers/Scenario';
import { PointBadge } from '../../helpers/PointBadge';

export type ChainDetailProps = {
  id: string;
  scenario: Scenario;
  chain: OperationChain;
  chainNumber: number;
};

export const ChainDetail: React.FC<ChainDetailProps> = ({
  scenario,
  chain,
}) => {
  return (
    <div className="row">
      <div className="col-12">
        <table className="w-100 table table-bordered my-3">
          <thead>
            <tr>
              <th>TSP</th>
              <th>Receipt</th>
              <th>Delivery</th>
              <th>Contract</th>
              <th>Priority</th>
              <th>Transport Cost</th>
            </tr>
          </thead>
          <tbody>
            {chain.chain_links.map((chain_link, index) => {
              const op_id = chain_link.operation_id;
              const operation = scenario.operations.find(
                (op) => op.id === op_id,
              );
              if (
                !operation ||
                (!operation.contract_id &&
                  index !== 0 &&
                  index !== chain.operations.length - 1)
              ) {
                return null;
              }

              const tportCost = operation.transport_cost_per_dth;
              const fuelCost =
                chain.fuel_makeup_price * (operation.fuel_factor - 1);
              const ftCost = parseFloat(tportCost?.toString()) + fuelCost;

              return (
                <tr key={op_id}>
                  <td>{operation.receipt_point.tsp_name}</td>
                  <td
                    style={{
                      maxWidth: '350px',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    <div className="d-flex mb-3 justify-content-center">
                      <PointBadge point={operation.receipt_point} />
                    </div>
                    {chain_link.receipt_description ??
                      chain_link.receipt_summary}
                    <br />
                  </td>
                  <td
                    style={{
                      maxWidth: '350px',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    <div className="d-flex mb-3 justify-content-center">
                      <PointBadge point={operation.delivery_point} />
                    </div>
                    {chain_link.delivery_description ??
                      chain_link.delivery_summary}
                    <br />
                  </td>
                  <td>
                    {operation.contract_id ? (
                      <>
                        <a
                          className="mb-2"
                          href={`/contracts/?edit_contract=${operation.contract_id}`}
                          target="_blank"
                        >
                          {operation.contract_name || operation.contract_k_id}
                        </a>
                        <br />
                        <LinkToRates operation={operation} />
                      </>
                    ) : (
                      <span className="text-danger">Not Found</span>
                    )}
                  </td>
                  <td>
                    {operation.transport_priority || (
                      <span className="text-muted">N/A</span>
                    )}
                  </td>
                  <td style={{ minWidth: '256px' }}>
                    <div className="row">
                      <div className="col-6">Variable:</div>
                      <div className="col text-end">
                        ${numberFormatter(tportCost, 4)}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-6">Fuel:</div>
                      <div
                        className="col text-end d-inline-flex align-items-center justify-content-end"
                        id={`tooltip-${operation.id}`}
                      >
                        <HelpCircleIcon
                          className="text-muted"
                          style={{ height: '18px', textAlign: 'center' }}
                        />{' '}
                        <span>
                          +<u>${numberFormatter(fuelCost, 4)}</u>
                        </span>
                      </div>
                    </div>
                    <div className="row fw-bold">
                      <div className="col-6">Total:</div>
                      <div className="col text-end">
                        ${numberFormatter(ftCost, 4)}
                      </div>
                    </div>
                    <UncontrolledTooltip target={`tooltip-${operation.id}`}>
                      <div>
                        {numberFormatter(chain.fuel_makeup_price, 4)} *{' '}
                        {numberFormatter(
                          100 *
                            (parseFloat(
                              operation.fuel_factor?.toString() || '1',
                            ) -
                              1),
                          2,
                        )}
                        %
                      </div>
                    </UncontrolledTooltip>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
